import { useState, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { useLanguage, useLp } from '../context/LanguageContext';
import { SEO } from '../components/SEO';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';

const FAQPage = () => {
  const { t, language } = useLanguage();
  const lp = useLp();
  const [extra, setExtra] = useState([]);
  useEffect(() => {
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/faq`, { params: { lang: language } })
      .then((r) => setExtra(r.data)).catch(() => setExtra([]));
  }, [language]);
  const base = t('faq.items');
  const faqItems = [...(Array.isArray(base) ? base : []), ...extra];

  return (
    <div data-testid="faq-page" className="min-h-screen">
      <SEO page="faq" />
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-[#121826]" />
        <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-[#C9A227]/40 to-transparent" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <p className="text-xs font-mono uppercase tracking-[0.25em] text-[#C9A227] font-semibold mb-4">
              Accordini Carillo
            </p>
            <h1 className="font-['Playfair_Display'] text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight mb-6 max-w-3xl">
              {t('faq.title')}
            </h1>
            <p className="text-white/45 text-base md:text-lg max-w-2xl leading-relaxed">
              {t('faq.subtitle')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="py-16 md:py-24 bg-[#FAF8F5]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Accordion type="single" collapsible className="space-y-4">
              {Array.isArray(faqItems) && faqItems.map((item, index) => (
                <AccordionItem 
                  key={index} 
                  value={`item-${index}`}
                  data-testid={`faq-item-${index}`}
                  className="border border-slate-200 px-6 data-[state=open]:border-[#C9A227]/50"
                >
                  <AccordionTrigger className="text-left font-['Playfair_Display'] text-lg font-medium text-[#283880] hover:text-[#C9A227] py-6">
                    {item.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-[#64748B] leading-relaxed pb-6">
                    {item.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </motion.div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-20 bg-[#F8F9FA]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-['Playfair_Display'] text-2xl md:text-3xl font-bold text-[#283880] mb-4">
              {t('hero.cta')}
            </h2>
            <p className="text-[#64748B] mb-8">
              {t('about.team')}
            </p>
            <a
              href={lp('/contatti')}
              data-testid="faq-contact-cta"
              className="inline-block bg-[#C9A227] text-[#283880] hover:bg-[#B8911F] h-12 px-8 uppercase tracking-widest text-xs font-bold leading-[48px] transition-colors"
            >
              {t('nav.contact')}
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default FAQPage;
